import { DateTime } from 'luxon';
import type {
  DemographicEligibilityCheckRequest,
  EligibilityProfileCandidate,
  EligibilityViewStatus,
  RegistrationGate,
} from '../eligibility/schema';

/**
 * Pure flow logic for the registration eligibility + demographic stage: the
 * screen ordering, the cascade short-circuit, the check-resubmission
 * composition, and the inverse-contact rule. Kept hook-free so every branch is
 * unit-testable with plain values.
 */

export type RegistrationScreen =
  | 'eligibility-confirm'
  | 'eligibility-employer-search'
  | 'eligibility-benefit-holder'
  | 'eligibility-name'
  | 'eligibility-contact'
  | 'eligibility-address'
  | 'eligibility-sex-at-birth'
  | 'eligibility-no-match'
  | 'eligibility-under-age'
  | 'eligibility-minor-blocked';

/** The values the flow accumulates across screens. */
export interface RegistrationFlowValues {
  lastName: string;
  dateOfBirth: string;
  companySelection: { ref: string; name: string } | null;
  relationship: 'primary' | 'dependent' | null;
  firstName: string;
  contactEmail: string;
  contactPhone: string;
  address: { line1: string; line2: string; city: string; state: string; zip: string };
  sexAtBirth: 'male' | 'female' | null;
}

export const EMPTY_FLOW_VALUES: RegistrationFlowValues = {
  lastName: '',
  dateOfBirth: '',
  companySelection: null,
  relationship: null,
  firstName: '',
  contactEmail: '',
  contactPhone: '',
  address: { line1: '', line2: '', city: '', state: '', zip: '' },
  sexAtBirth: null,
};

/**
 * Which screen follows `current`. An `eligible` resolution short-circuits the
 * remaining disambiguators (employer, benefit-holder) — the demographic
 * screens always run, prefilled from the candidate. The terminal decision
 * after the sex screen belongs to `terminalAfterSex` below, not this table.
 */
export function nextScreen(
  current: Exclude<
    RegistrationScreen,
    | 'eligibility-sex-at-birth'
    | 'eligibility-no-match'
    | 'eligibility-under-age'
    | 'eligibility-minor-blocked'
  >,
  eligible: boolean,
): RegistrationScreen {
  switch (current) {
    case 'eligibility-confirm':
      return eligible ? 'eligibility-name' : 'eligibility-employer-search';
    case 'eligibility-employer-search':
      return eligible ? 'eligibility-name' : 'eligibility-benefit-holder';
    case 'eligibility-benefit-holder':
      return 'eligibility-name';
    case 'eligibility-name':
      return 'eligibility-contact';
    case 'eligibility-contact':
      return 'eligibility-address';
    case 'eligibility-address':
      return 'eligibility-sex-at-birth';
  }
}

/**
 * The flow's terminal after the sex screen: a resolved non-eligible outcome
 * lands on the no-match screen (retry / support / proceed-anyway); anything
 * else — eligible, or a still-pending check (registration never blocks on the
 * async verifier) — completes the stage.
 */
export function terminalAfterSex(
  status: EligibilityViewStatus,
  gate: RegistrationGate = 'proceed',
): 'complete' | 'eligibility-no-match' | 'eligibility-under-age' | 'eligibility-minor-blocked' {
  const blocked = gateScreen(gate);
  if (blocked) return blocked;
  return status === 'needs_review' ? 'eligibility-no-match' : 'complete';
}

/**
 * The server-decided terminal for a blocking registration gate; null means the
 * gate does not block. The client never inspects age or minor status — this
 * mapping is the entire branch.
 */
export function gateScreen(
  gate: RegistrationGate,
): 'eligibility-under-age' | 'eligibility-minor-blocked' | null {
  if (gate === 'under_age') return 'eligibility-under-age';
  if (gate === 'minor_unsupported') return 'eligibility-minor-blocked';
  return null;
}

/** The signup OTP channel; the contact screen collects the inverse. */
export type SignupChannel = 'email' | 'phone';

/**
 * Which contact field the contact screen collects: the inverse of the signup
 * OTP channel. An unknown channel (reinstall mid-flow, legacy account)
 * defaults to collecting email — the strongest match input.
 */
export function inverseContactChannel(signupChannel: SignupChannel | null): SignupChannel {
  return signupChannel === 'email' ? 'phone' : 'email';
}

/**
 * Compose a check resubmission from everything captured so far. Fields ride
 * every resubmission once captured (the server treats each check as a fresh
 * match attempt); `email` rides only when the collected inverse channel is
 * email (a collected phone is not a match input).
 */
export function composeCheckRequest(
  values: RegistrationFlowValues,
): DemographicEligibilityCheckRequest {
  return {
    lastName: values.lastName,
    dateOfBirth: values.dateOfBirth,
    ...(values.companySelection ? { clientRef: values.companySelection.ref } : {}),
    ...(values.relationship ? { relationship: values.relationship } : {}),
    ...(values.firstName.trim() ? { firstName: values.firstName.trim() } : {}),
    ...(values.contactEmail.trim() ? { email: values.contactEmail.trim() } : {}),
  };
}

/** Client-side email shape check (the server re-validates). */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) && value.trim().length <= 254;
}

/** Client-side E.164-normalizable phone check: US 10-digit or +-prefixed. */
export function normalizePhone(value: string): string | null {
  const digits = value.replace(/[^0-9+]/g, '');
  if (/^\+[1-9]\d{6,14}$/.test(digits)) return digits;
  const bare = digits.replace(/^\+/, '');
  if (/^[0-9]{10}$/.test(bare)) return `+1${bare}`;
  if (/^1[0-9]{10}$/.test(bare)) return `+${bare}`;
  return null;
}

/** Full US phone: 10 digits with NANP leading digits (area + exchange 2-9). */
export function isValidUsPhone(value: string): boolean {
  return /^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(value.replace(/[^0-9]/g, ''));
}

/**
 * Extract the 10 national digits from a stored US E.164 (or bare 10-digit)
 * phone for prefill; null for anything else — a non-US candidate value simply
 * leaves the field blank rather than mis-formatting.
 */
export function usPhoneDigits(value: string): string | null {
  const digits = value.replace(/[^0-9+]/g, '').replace(/^\+?1/, '');
  return /^[0-9]{10}$/.test(digits) ? digits : null;
}

/** Progressive `(416) 889-0909` input mask: digits in, punctuation managed here. */
export function formatUsPhoneInput(value: string): string {
  const raw = value.replace(/[^0-9]/g, '');
  // Browser autofill hands over the stored E.164 form (`+14168890909`); the
  // leading US country code drops so the mask agrees with `usPhoneDigits`
  // (the prefill path). Only an eleven-digit `1…` value qualifies: shorter
  // input is still being typed and a ten-digit `1…` value is left for
  // `isValidUsPhone` to reject (no NANP area code starts with 1).
  const digits = (raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw).slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Parse a member-typed date of birth (`MM/DD/YYYY`) into an ISO calendar date;
 * null when not a real past calendar date. Luxon parses non-leniently
 * (ADR-096), so impossible dates never roll over.
 */
export function parseDateOfBirth(value: string): string | null {
  const parsed = DateTime.fromFormat(value.trim(), 'MM/dd/yyyy', { zone: 'utc' });
  if (!parsed.isValid) return null;
  const iso = parsed.toISODate();
  if (!iso) return null;
  const today = DateTime.now().setZone('utc').toISODate();
  if (today && iso > today) return null;
  return iso;
}

/** Progressive `MM/DD/YYYY` input mask: digits in, slashes managed here. */
export function formatDateOfBirthInput(value: string): string {
  const raw = value.replace(/[^0-9]/g, '');
  // Browsers fill `bday` as ISO `YYYY-MM-DD`; exactly eight digits opening
  // with a 19xx/20xx year is read year-first and reordered to MM/DD/YYYY. No
  // calendar month is 19 or 20, so a month-first value can never take this
  // branch and digit-by-digit typing is untouched.
  const digits = (
    raw.length === 8 && /^(19|20)/.test(raw)
      ? `${raw.slice(4, 6)}${raw.slice(6, 8)}${raw.slice(0, 4)}`
      : raw
  ).slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Prefill the flow values from an eligible candidate: candidate values fill
 * only fields the member has not already entered — member input always wins.
 */
export function applyCandidate(
  values: RegistrationFlowValues,
  candidate: EligibilityProfileCandidate | null | undefined,
): RegistrationFlowValues {
  if (!candidate) return values;
  return {
    ...values,
    firstName: values.firstName || (candidate.firstName ?? ''),
    lastName: values.lastName || (candidate.lastName ?? ''),
    contactEmail: values.contactEmail || (candidate.email ?? ''),
    contactPhone: values.contactPhone || (candidate.phone ?? ''),
    address: {
      line1: values.address.line1 || (candidate.address?.line1 ?? ''),
      line2: values.address.line2 || (candidate.address?.line2 ?? ''),
      city: values.address.city || (candidate.address?.city ?? ''),
      state: values.address.state || (candidate.address?.state ?? ''),
      zip: values.address.zip || (candidate.address?.zip ?? ''),
    },
    sexAtBirth:
      values.sexAtBirth ??
      (candidate.sexAssignedAtBirth === 'male' || candidate.sexAssignedAtBirth === 'female'
        ? candidate.sexAssignedAtBirth
        : null),
  };
}
